<template>
  <ShipmentMap
    v-if="shipment?.id"
    :id="mapId"
    :shipment="shipment"
    :events="shipmentEvents"
    :static-map="staticMapUrl.data"
    :access-token="accessToken"
    :loading="isAnyLoading || staticMapUrl.loading"
    :dynamic="showDynamicMap"
    :interactive="isMapActionsEnabled"
  />
  <Content
    id="carrier-portal-spot-market"
    class="carrier-portal"
    :header-dark-bg="!!shipment?.id"
    :header-label="
      shipmentId
        ? t('shipments.detailsForShipmentId', { shipmentId })
        : t('orders.detailsForOrderId', { orderId })
    "
    :loading="isAnyLoading"
    header-icon="map-marked-alt"
    remove-card-padding
    remove-aside-card-wrapper
  >
    <template #headerRight>
      <div class="spot-market-actions flex flex-gap-1 align-items-center">
        <RygenButton
          v-if="!spotMarket.data?.isCompleted"
          :label="t('common.rejectParam', { param: t('shipments.shipments') })"
          :severity="Severity.DANGER"
          :disabled="
            hasAcceptedQuote || spotMarket.loading || !!spotMarket.error
          "
          icon="pi pi-times"
          @click="onReject"
        />
      </div>
    </template>

    <template #aside>
      <SpotMarketRateShopSection
        id="rate-shop"
        :spot-market="spotMarket"
        :carrier-setting="carrierSetting"
        :accessorial-codes="accessorialCodes"
        :disabled="
          spotMarket.data?.isCompleted ||
          hasAcceptedQuote ||
          isHumanReadableError(spotMarket.error)
        "
        @delete="
          requestDeleteConfirmation({
            param: t('carrierPortal.spotMarket.bids'),
            accept: () => onDeleteQuote($event),
          })
        "
        @save="onSaveQuote"
      />
    </template>

    <template #secondaryAside>
      <Card class="no-pad">
        <template #content>
          <ReferenceNumbersSection
            v-if="
              shipmentId &&
              isCarrierAssignedToShipment &&
              allReferenceNumbers.length
            "
            id="reference-numbers"
            v-model="allReferenceNumbers"
            :resource-id="shipmentId"
            :editable="false"
          />

          <CarrierPortalAccessorialSection
            v-if="shipment?.requestedAccessorials?.length"
            id="accessorials"
            :accessorials="shipment?.requestedAccessorials"
          />

          <BillToSection
            id="bill-to"
            :bill-to="shipment?.billTo"
            :editable="false"
          />

          <CarrierPortalContactSection
            v-if="shipmentSummary?.shipmentEmailContact"
            id="contact"
            :contact="shipmentSummary?.shipmentEmailContact"
          />

          <CarrierPortalNotesSection
            v-if="
              isCarrierAssignedToShipment ||
              spotMarket.data?.includeCarrierNotes
            "
            id="notes"
            :carrier-notes="shipment?.carrierNotes"
          />
        </template>
      </Card>
    </template>

    <BlockUI :blocked="loading" full-screen />

    <CarrierPortalHighlightSection
      id="highlights"
      :shipment="shipment"
      :accepted-rate-quote="acceptedRateQuote"
    />

    <section v-if="!isAnyLoading" class="error-section detail-section">
      <MessageContainer :messages="messages" message-class="rygen-message" />
    </section>

    <CarrierPortalLocationSection
      id="locations"
      :shipment="shipment"
      :hide-notes="
        !isCarrierAssignedToShipment && !spotMarket.data?.includeLocationNotes
      "
      :show-limited-location="!isCarrierAssignedToShipment"
    />

    <CarrierPortalTimelineSection
      v-if="isCarrierAssignedToShipment && shipmentEvents.length"
      id="events"
      :shipment-events="shipmentEvents"
      :shipment-service-incidents="serviceIncidents"
      :shipment="shipment"
      :shipment-stops="shipmentStops"
      :tender-requests="
        shipmentSummary?.tenderRequest ? [shipmentSummary.tenderRequest] : []
      "
      @save="loadShipmentSummary"
    />

    <CarrierPortalLineItemSection
      id="line-items"
      :line-items="shipment?.lineItems"
    />
  </Content>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { usePrimeVue } from 'primevue/config';
import { useConfirm } from 'primevue/useconfirm';
import type { PropType, Ref } from 'vue';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  toRef,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import authorization from '@rygen/rygen-ui/authorization';
import { useDeleteConfirmation, useToast } from '@rygen/rygen-ui/composables';
import {
  AccessorialCode,
  CarrierSetting,
  Loadable,
  Severity,
  UUID,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useMap } from '@/composables';
import {
  Carrier,
  CarrierShipmentSummary,
  isHumanReadableError,
  MessageSpecification,
  RateQuote,
  SpotMarket,
  SpotMarketExceptionReason,
  SpotMarketStatus,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { getErrorDetail, isConflict } from '@/util';

import CarrierPortalAccessorialSection from '@/components/carrierPortal/CarrierPortalAccessorialSection.vue';
import CarrierPortalContactSection from '@/components/carrierPortal/CarrierPortalContactSection.vue';
import CarrierPortalHighlightSection from '@/components/carrierPortal/CarrierPortalHighlightSection.vue';
import CarrierPortalLineItemSection from '@/components/carrierPortal/CarrierPortalLineItemSection.vue';
import CarrierPortalLocationSection from '@/components/carrierPortal/CarrierPortalLocationSection.vue';
import CarrierPortalNotesSection from '@/components/carrierPortal/CarrierPortalNotesSection.vue';
import CarrierPortalTimelineSection from '@/components/carrierPortal/event/CarrierPortalTimelineSection.vue';
import { type BidPayload } from '@/components/carrierPortal/rateShop/SpotMarketBidEdit.vue';
import SpotMarketRateShopSection from '@/components/carrierPortal/rateShop/SpotMarketRateShopSection.vue';
import BillToSection from '@/components/location/BillToSection.vue';
import ReferenceNumbersSection from '@/components/referenceNumber/ReferenceNumbersSection.vue';
import ShipmentMap from '@/components/shipment/ShipmentMap.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import { MessageProps } from 'primevue/message';

const props = defineProps({
  shipmentId: {
    type: Number,
    default: () => undefined,
  },
  orderId: {
    type: Number,
    default: () => undefined,
  },
  shipmentSpecId: {
    type: Number,
    default: () => undefined,
  },
  action: {
    type: String as PropType<'spotMarketReject'>,
    default: () => '',
  },
  accessToken: {
    type: String as PropType<UUID>,
    required: true,
  },
});
const { shipmentId, orderId, shipmentSpecId, action, accessToken } =
  toRefs(props);

const confirm = useConfirm();
const { isAuthenticatedUser, loadDomainForUnauthenticatedUser } =
  useCurrentUserStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { t } = useI18n();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const router = useRouter();
const toast = useToast();

const shipmentSummary: Ref<CarrierShipmentSummary | undefined> = ref();
const shipment = toRef(() => shipmentSummary.value?.shipment);
const { isMapActionsEnabled, mapId, showDynamicMap, staticMapUrl } =
  useMap(shipment);

const loading = ref(false);
const carrierSetting: Ref<Loadable<CarrierSetting>> = ref({ loading: false });
const accessorialCodes: Ref<Loadable<AccessorialCode[]>> = ref({
  loading: false,
});

const spotMarket: Ref<Loadable<SpotMarket>> = ref({ loading: false });

const shipmentStops = toRef(() => shipment.value?.stops ?? []);
const acceptedRateQuote = toRef(() => shipmentSummary.value?.acceptedRateQuote);
const shipmentEvents = toRef(() => shipmentSummary.value?.shipmentEvents ?? []);
const serviceIncidents = toRef(() => shipmentSummary.value?.serviceIncidents);

const hasAcceptedQuote = toRef(
  () => !!shipmentSummary.value?.acceptedRateQuote,
);

const isCarrierAssignedToShipment = computed(() => {
  if (!hasAcceptedQuote.value || !shipmentSummary.value?.carrier?.id) {
    return false;
  }

  return shipmentSummary.value.carrier.id === shipment.value?.carrierId;
});

const isAnyLoading = computed(() => loading.value || spotMarket.value.loading);

const allReferenceNumbers = computed(() => {
  const shipmentReferenceNumbers = shipment.value?.referenceNumbers ?? [];
  const shipmentLineItemReferenceNumbers =
    shipment.value?.lineItems.map((li) => li.referenceNumbers).flat() ?? [];
  return [...shipmentReferenceNumbers, ...shipmentLineItemReferenceNumbers];
});

const messages = computed(() => {
  const messageSpecifications: MessageSpecification[] = [
    {
      id: 'closed-warning',
      text: t('carrierPortal.spotMarket.closed'),
      severity: 'warn',
      visible: hasAcceptedQuote.value || spotMarket.value.data?.isClosed,
    },
    {
      id: 'rejected-warning',
      text: t('carrierPortal.spotMarket.rejected'),
      severity: 'warn',
      visible: spotMarket.value.data?.isRejected,
    },
    {
      id: 'expired-warning',
      text: t('carrierPortal.spotMarket.expired'),
      severity: 'warn',
      visible: spotMarket.value.data?.isExpired,
    },
    {
      id: 'cancelled-warning',
      text: t('carrierPortal.spotMarket.cancelled'),
      severity: 'warn',
      visible: spotMarket.value.data?.isCancelled,
    },
  ];

  if (isHumanReadableError(spotMarket.value.error)) {
    messageSpecifications.push({
      id: 'server-error',
      text: spotMarket.value.error.message,
      severity: convertSeverityToMessageSeverity(
        spotMarket.value.error.severity,
      ),
      icon: 'pi pi-exclamation-triangle',
      visible: true,
    });
  }

  return messageSpecifications;
});

const loadShipmentSummaryForShipmentSpec = async (shipmentSpecId: number) => {
  try {
    loading.value = true;
    shipmentSummary.value = await api.carrierPortal.getShipmentSpecSummary(
      shipmentSpecId,
      {
        accessToken: accessToken.value,
        withTenderRequest: false,
      },
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading shipment', e);
    toast.addError({
      detail: t('errors.loadingParamWithId', {
        param: t('shipmentSpecs.shipmentSpecs'),
        id: shipmentSpecId,
      }),
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

const loadShipmentSummaryForShipment = async (shipmentId: number) => {
  try {
    loading.value = true;
    shipmentSummary.value = await api.carrierPortal.getShipmentSummary(
      shipmentId,
      {
        accessToken: accessToken.value,
        withTenderRequest: false,
      },
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading shipment', e);
    toast.addError({
      detail: t('errors.loadingParamWithId', {
        param: t('shipments.shipments'),
        id: shipmentId,
      }),
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

const loadShipmentSummary = async () => {
  if (shipmentId?.value) {
    await loadShipmentSummaryForShipment(shipmentId.value);
  } else if (shipmentSpecId?.value) {
    await loadShipmentSummaryForShipmentSpec(shipmentSpecId.value);
  } else {
    throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
  }

  // TODO: check shipmentSummary.value?.errorMessage to determine if we need to show a Message

  const ownerId = shipmentSummary.value?.shipment?.owner?.id;
  if (ownerId) {
    loadDomainForUnauthenticatedUser(ownerId, accessToken.value);
  }
};

const loadSpotMarket = async (carrierId: number) => {
  try {
    if (!accessToken.value) {
      throw new Error('missing access token');
    }

    spotMarket.value.loading = true;
    if (shipmentId?.value) {
      spotMarket.value.data = isAuthenticatedUser.value
        ? await api.shipment.loadSpotMarket(shipmentId.value, carrierId)
        : await api.carrierPortal.loadSpotMarketForShipment(
            shipmentId.value,
            carrierId,
            accessToken.value,
          );
    } else if (shipmentSpecId?.value) {
      spotMarket.value.data = isAuthenticatedUser.value
        ? await api.shipmentSpec.loadSpotMarket(shipmentSpecId.value, carrierId)
        : await api.carrierPortal.loadSpotMarketForShipmentSpec(
            shipmentSpecId.value,
            carrierId,
            accessToken.value,
          );
    } else {
      throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
    }

    if (spotMarket.value.data?.status === SpotMarketStatus.REQUESTED) {
      logFirstSpotMarketVisit(spotMarket.value.data.id!);
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading spot market', e);
    spotMarket.value.error = e;
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('carrierPortal.spotMarket.spotMarkets'),
      }),
      error: e,
    });
  } finally {
    spotMarket.value.loading = false;
  }
};

const logFirstSpotMarketVisit = async (spotMarketId: number) => {
  try {
    if (!accessToken.value) {
      throw new Error('missing access token');
    }

    spotMarket.value.loading = true;
    if (shipmentId?.value) {
      isAuthenticatedUser.value
        ? await api.shipment.logFirstSpotMarketVisit(
            shipmentId.value,
            spotMarketId,
          )
        : await api.carrierPortal.logFirstSpotMarketVisitForShipment(
            shipmentId.value,
            spotMarketId,
            accessToken.value,
          );
    } else if (shipmentSpecId?.value) {
      isAuthenticatedUser.value
        ? await api.shipmentSpec.logFirstSpotMarketVisit(
            shipmentSpecId.value,
            spotMarketId,
          )
        : await api.carrierPortal.logFirstSpotMarketVisitForShipmentSpec(
            shipmentSpecId.value,
            spotMarketId,
            accessToken.value,
          );
    } else {
      throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // We don't really care about the first visit. Log the error and move on
    console.error('Error logging first spot market visit', e);
  } finally {
    spotMarket.value.loading = false;
  }
};

const loadCarrierSettings = async (carrier: Carrier) => {
  try {
    if (!carrier.scac) {
      throw new Error('missing scac');
    }

    carrierSetting.value.loading = true;
    if (
      isAuthenticatedUser.value &&
      authorization.hasAuthority('client', 'read')
    ) {
      carrierSetting.value.data = await api.carrier.findCarrierSettingsForScac(
        carrier.scac,
      );
    } else {
      if (!accessToken.value) {
        throw new Error('missing access token');
      }

      if (shipmentId?.value) {
        carrierSetting.value.data =
          await api.carrierPortal.findCarrierSettingsForScacAndShipment(
            carrier.scac,
            shipmentId.value,
            accessToken.value,
          );
      } else if (shipmentSpecId?.value) {
        carrierSetting.value.data =
          await api.carrierPortal.findCarrierSettingsForScacAndShipmentSpec(
            carrier.scac,
            shipmentSpecId.value,
            accessToken.value,
          );
      } else {
        throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
      }
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error("Error loading selected carrier's settings", e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.carrierSettings', 2),
      }),
      error: e,
    });
  } finally {
    carrierSetting.value.loading = false;
  }
};

const loadRequestableAccessorialCodes = async () => {
  try {
    accessorialCodes.value.loading = true;
    if (isAuthenticatedUser.value) {
      accessorialCodes.value.data =
        await api.accessorialCode.findAllRequestable();
    } else {
      if (!accessToken.value) {
        throw new Error('missing access token');
      }

      if (shipmentId?.value) {
        accessorialCodes.value.data =
          await api.carrierPortal.findAllRequestableAccessorialCodesByShipmentId(
            shipmentId.value,
            accessToken.value,
          );
      } else if (shipmentSpecId?.value) {
        accessorialCodes.value.data =
          await api.carrierPortal.findAllRequestableAccessorialCodesByShipmentSpecId(
            shipmentSpecId.value,
            accessToken.value,
          );
      } else {
        throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
      }
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    accessorialCodes.value.error = e;
    console.error('Error loading accessorial codes', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.additionalParam', {
          param: t('shipments.services', 2),
        }),
      }),
      error: e,
    });
  } finally {
    accessorialCodes.value.loading = false;
  }
};

const onRejectResolution = () => {
  const query = { ...router.currentRoute.value.query };
  delete query.action;

  router.push({
    query,
  });
};

const onReject = () => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('common.rejectParam', { param: t('shipments.shipments') }),
    message: t('carrierPortal.spotMarket.rejectConfirmation'),
    icon: 'pi pi-exclamation-triangle text-danger',
    accept: onConfirmReject,
    acceptClass: 'p-button-sm p-button-danger',
    acceptIcon: 'pi pi-times',
    acceptLabel: t('common.reject'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.cancel'),
  });
};

const onConfirmReject = async () => {
  const spotMarketId = spotMarket.value.data?.id;
  if (!spotMarketId || !accessToken.value) {
    return;
  }

  try {
    spotMarket.value.loading = true;
    if (shipmentId?.value) {
      isAuthenticatedUser.value
        ? await api.shipment.rejectSpotMarket(shipmentId.value, spotMarketId)
        : await api.carrierPortal.rejectSpotMarketForShipment(
            shipmentId.value,
            spotMarketId,
            accessToken.value,
          );
    } else if (shipmentSpecId?.value) {
      isAuthenticatedUser.value
        ? await api.shipmentSpec.rejectSpotMarket(
            shipmentSpecId.value,
            spotMarketId,
          )
        : await api.carrierPortal.rejectSpotMarketForShipmentSpec(
            shipmentSpecId.value,
            spotMarketId,
            accessToken.value,
          );
    } else {
      throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
    }

    // The guard at the top of the function ensures `data` is defined
    spotMarket.value.data!.isRejected = true;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    spotMarket.value.error = e;
    console.error('Error rejecting spot market', e);
    toast.addError({
      detail: t('errors.rejectingParam', { param: t('shipments.shipments') }),
      error: e,
    });
  } finally {
    spotMarket.value.loading = false;
  }
};

const onDeleteQuote = async (rateQuote: RateQuote) => {
  try {
    if (!accessToken.value) {
      throw new Error('missing access token');
    }

    const spotMarketId = spotMarket.value.data?.id;
    if (!spotMarketId) {
      throw new Error('missing spot market id');
    }

    if (!rateQuote.id) {
      throw new Error('Cannot delete rate quote without id');
    }

    spotMarket.value.loading = true;
    if (shipmentId?.value) {
      isAuthenticatedUser.value
        ? await api.shipment.deleteSpotMarketBid(
            shipmentId.value,
            spotMarketId,
            rateQuote.id,
          )
        : await api.carrierPortal.deleteSpotMarketBidForShipment(
            shipmentId.value,
            spotMarketId,
            rateQuote.id,
            accessToken.value,
          );
    } else if (shipmentSpecId?.value) {
      isAuthenticatedUser.value
        ? await api.shipmentSpec.deleteSpotMarketBid(
            shipmentSpecId.value,
            spotMarketId,
            rateQuote.id,
          )
        : await api.carrierPortal.deleteSpotMarketBidForShipmentSpec(
            shipmentSpecId.value,
            spotMarketId,
            rateQuote.id,
            accessToken.value,
          );
    } else {
      throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
    }

    // The guard at the top of the function ensures `data` is defined
    const index = spotMarket.value.data!.bids.findIndex(
      (r) => r.id === rateQuote.id,
    );
    spotMarket.value.data!.bids.splice(index, 1);
    toast.addSuccess({
      detail: t('messages.successDeletingParam', {
        param: t('carrierPortal.spotMarket.bids'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    spotMarket.value.error = e;
    console.error('Error deleting spot market bid', e);
    toast.addError({
      detail: t('errors.deletingParam', {
        param: t('carrierPortal.spotMarket.bids'),
      }),
      error: e,
    });
  } finally {
    spotMarket.value.loading = false;
  }
};

const onSaveQuote = async ({
  bid,
  onSuccessCallback,
  onErrorCallback,
}: BidPayload) => {
  try {
    if (!accessToken.value) {
      throw new Error('missing access token');
    }

    const spotMarketId = spotMarket.value.data?.id;
    if (!spotMarketId) {
      throw new Error('missing spot market id');
    }

    if (!bid.spotMarketId) {
      bid.spotMarketId = spotMarketId;
    }

    spotMarket.value.loading = true;
    let createdBid: RateQuote | undefined;
    if (shipmentId?.value) {
      createdBid = isAuthenticatedUser.value
        ? await api.shipment.addSpotMarketBid(
            shipmentId.value,
            spotMarketId,
            bid,
          )
        : await api.carrierPortal.addSpotMarketBidForShipment(
            shipmentId.value,
            spotMarketId,
            bid,
            accessToken.value,
          );
    } else if (shipmentSpecId?.value) {
      createdBid = isAuthenticatedUser.value
        ? await api.shipmentSpec.addSpotMarketBid(
            shipmentSpecId.value,
            spotMarketId,
            bid,
          )
        : await api.carrierPortal.addSpotMarketBidForShipmentSpec(
            shipmentSpecId.value,
            spotMarketId,
            bid,
            accessToken.value,
          );
    } else {
      throw new Error('One of `shipmentId` or `shipmentSpecId` is required');
    }

    onSuccessCallback();
    // The guard at the top of the function ensures `data` is defined
    spotMarket.value.data!.bids.push(createdBid);
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('carrierPortal.spotMarket.bids'),
      }),
    });
  } catch (e) {
    onErrorCallback();
    if (axios.isCancel(e)) {
      return;
    }

    spotMarket.value.error = e;
    console.error('Error saving spot market bid', e);

    if (isConflict(e)) {
      const reasonCode = getErrorDetail(
        e,
        'reasonCode',
      ) as SpotMarketExceptionReason;
      const isSuccessfulReason =
        reasonCode === SpotMarketExceptionReason.ALREADY_ACCEPTED_SAME_CARRIER;
      spotMarket.value.error = {
        message: t(`enumMap.SpotMarketExceptionReason.${reasonCode}`),
        reasonCode,
        severity: isSuccessfulReason ? Severity.SUCCESS : Severity.DANGER,
        isHumanReadableError: true,
      };

      if (isSuccessfulReason) {
        return;
      }
    }

    toast.addError({
      detail: t('errors.savingParam', {
        param: t('carrierPortal.spotMarket.bids'),
      }),
      error: e,
    });
  } finally {
    spotMarket.value.loading = false;
  }
};

const convertSeverityToMessageSeverity = (
  severity: Severity,
): MessageProps['severity'] => {
  switch (severity) {
    case Severity.SUCCESS:
    case Severity.SUCCESS_LIGHT:
      return 'success';
    case Severity.INFO:
    case Severity.INFO_LIGHT:
      return 'info';
    case Severity.WARNING:
    case Severity.WARNING_LIGHT:
      return 'warn';
    case Severity.DANGER:
    case Severity.DANGER_LIGHT:
      return 'error';
  }
};

onMounted(async () => {
  setParentEntity(shipment);
  await loadShipmentSummary();

  if (shipmentSummary.value) {
    if (shipmentSummary.value.carrier?.id) {
      await loadSpotMarket(shipmentSummary.value.carrier.id);
      if (action.value === 'spotMarketReject') {
        // Defer opening the confirm modal until after the view has rendered so
        // the confirm dialog is registered, then strip the `action` query param
        // only once the modal is open to avoid the re-render losing the request.
        await nextTick();
        onReject();
        await nextTick();
        onRejectResolution();
        return;
      }

      loadCarrierSettings(shipmentSummary.value.carrier);
    }

    loadRequestableAccessorialCodes();
  }
});

onUnmounted(() => {
  clearParentEntity();
});

defineExpose({
  $primevue: usePrimeVue(),
});
provide('shipmentStops', shipmentStops);
</script>

<style>
#carrier-portal-spot-market {
  .content-header {
    &.dark-bg {
      .spot-market-actions {
        .separator {
          color: white;
        }
      }
    }
  }

  .error-section:empty {
    display: none;
  }
}
</style>
